/**
 * The billing ticket edit form, as pure functions (#50 PR C, DEC-356).
 *
 * What may be edited mirrors the server (api services/billing/ticket-edit.ts), which stays
 * the authority — this only decides what to offer and builds the PATCH body:
 *
 *   volume   single-capture tickets only (OPEN-159). On a PER_BBL ticket it changes the
 *            bill; on any other it only corrects the reviewed volume ("doesn't change the
 *            bill").
 *   hours    HOURLY tickets with one unbilled hours line; quarter-hours.
 *   lines    unbilled extras and products: quantity, or remove.
 *   add      an active catalog charge plus a quantity, priced at the catalog rate.
 *   disposal fee (#52, DEC-358)  the fee line bills the ticket volume: its quantity follows
 *            a volume edit (on any billing method, so a volume edit with a fee line changes
 *            the bill) and is never typed; it can be removed. "Add disposal fee" adds the
 *            server's offer (the fee resolved for the ticket's day) to a ticket without one.
 *
 * Quantities stay decimal strings end to end; the live preview prices them with the shared
 * lineAmountCents, the helper the server and the invoice use, so preview = saved total.
 */

import { lineAmountCents } from '@lodex-pro/shared/money';
import type { DisposalFeeOffer, TicketLine, TicketPageResponse } from './hooks';

export const BBL_PATTERN = /^\d{1,8}(\.\d{1,2})?$/;
export const HOURS_PATTERN = /^\d{1,3}(\.(0|00|25|5|50|75))?$/;
export const QUANTITY_PATTERN = /^\d{1,7}(\.\d{1,3})?$/;
export const REASON_MIN = 10;
export const REASON_MAX = 500;

const positive = (s: string): boolean => /[1-9]/.test(s);

/** Canonical decimal string, so "2" and "2.000" compare equal. */
function canonical(s: string): string {
  const [i = '', f = ''] = s.trim().split('.');
  const int = i.replace(/^0+(?=\d)/, '') || '0';
  const frac = f.replace(/0+$/, '');
  return frac === '' ? int : `${int}.${frac}`;
}
export function sameDecimal(a: string, b: string): boolean {
  return canonical(a) === canonical(b);
}

export interface EditableLine {
  readonly eventId: string;
  readonly label: string;
  readonly unitLabel: string;
  readonly rateCents: number | null;
  /** The quantity the form loaded — sent back as `before` (the stale-save guard). */
  readonly loaded: string;
  value: string;
  removed: boolean;
  /** #52 — the disposal fee line: its quantity is the volume, so it is removable only. */
  readonly followsVolume: boolean;
}

export interface AddedExtra {
  readonly clientId: string;
  readonly serviceChargeId: string;
  readonly label: string;
  readonly rateCents: number;
  quantity: string;
}

export interface TicketEditForm {
  /** Null = volume isn't offered on this ticket. */
  readonly volume: {
    readonly loaded: string;
    value: string;
    /** False on a non-PER_BBL ticket: the edit corrects the record, not the bill. */
    readonly changesBill: boolean;
    readonly rateCents: number | null;
  } | null;
  readonly hours: {
    readonly loaded: string;
    value: string;
    readonly rateCents: number | null;
  } | null;
  lines: EditableLine[];
  adds: AddedExtra[];
  /** #52 — what "Add disposal fee" would add; null when the ticket takes none or has one. */
  readonly feeOffer: DisposalFeeOffer | null;
  addFee: boolean;
  reason: string;
}

/** The offer can be added: a fee is set and it is not a deliberate $0.00. */
export function feeAddable(offer: DisposalFeeOffer | null): offer is DisposalFeeOffer & {
  readonly feeCentsPerBbl: number;
} {
  return offer !== null && offer.feeCentsPerBbl !== null && offer.feeCentsPerBbl > 0;
}

const unbilled = (page: TicketPageResponse): TicketLine[] =>
  page.lines.filter((l) => l.invoice === null);

/** The form for a ticket, or null when the ticket isn't editable at all. */
export function initialForm(page: TicketPageResponse): TicketEditForm | null {
  if (!page.editable) return null;
  const open = unbilled(page);
  const volumeLines = open.filter((l) => l.kind === 'VOLUME');
  const billedVolume = page.lines.some((l) => l.kind === 'VOLUME' && l.invoice !== null);
  // #52 — a fee line bills the volume, so a volume edit changes the bill on any method.
  const hasFeeLine = open.some((l) => l.kind === 'DISPOSAL_FEE');

  let volume: TicketEditForm['volume'] = null;
  // OPEN-159: a ticket with more than one load is editable too. The volume is the ticket total
  // (the primary capture's review, else every capture's computed net), as billed.
  if (page.capture.captureCount >= 1 && !billedVolume && volumeLines.length <= 1) {
    const line = volumeLines[0];
    const loaded =
      line?.quantity ?? page.capture.reviewedNetBbl ?? page.capture.capturedNetBbl ?? '0';
    volume = {
      loaded,
      value: loaded,
      changesBill: (page.ticket.billingMethod === 'PER_BBL' && line !== undefined) || hasFeeLine,
      rateCents: line?.rateCents ?? null,
    };
  }

  const hourLines = open.filter((l) => l.kind === 'HOURS');
  const hourLine = hourLines[0];
  const hours =
    page.ticket.billingMethod === 'HOURLY' && hourLines.length === 1 && hourLine !== undefined
      ? { loaded: hourLine.quantity, value: hourLine.quantity, rateCents: hourLine.rateCents }
      : null;

  const lines = open
    .filter(
      (l) => l.kind === 'SERVICE_CHARGE' || l.kind === 'LINE_ITEM' || l.kind === 'DISPOSAL_FEE',
    )
    .map((l) => ({
      eventId: l.eventId,
      label: l.description,
      unitLabel: l.unitLabel,
      rateCents: l.rateCents,
      loaded: l.quantity,
      value: l.quantity,
      removed: false,
      followsVolume: l.kind === 'DISPOSAL_FEE',
    }));

  // OPEN-159: the fee bills the ticket total (the same rule as the volume above) and is keyed on
  // the primary capture, so a ticket with more than one load is offered it too.
  const feeOffer =
    page.capture.captureCount >= 1 && !hasFeeLine ? (page.disposalFeeOffer ?? null) : null;

  return { volume, hours, lines, adds: [], feeOffer, addFee: false, reason: '' };
}

/** The volume the fee bills after this save: the edited volume when valid, else `fallback`. */
function feeVolume(form: TicketEditForm, fallback: string): string {
  const v = form.volume?.value.trim();
  return v !== undefined && BBL_PATTERN.test(v) && positive(v) ? v : fallback;
}

/** Field → message; empty when the form can be saved. */
export function validate(form: TicketEditForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (form.volume !== null) {
    const v = form.volume.value.trim();
    if (!BBL_PATTERN.test(v)) errors.volume = 'Enter a volume with up to 2 decimals.';
    else if (!positive(v)) errors.volume = 'Volume must be greater than 0.';
  }
  if (form.hours !== null) {
    const h = form.hours.value.trim();
    if (!HOURS_PATTERN.test(h)) errors.hours = 'Enter hours in quarter-hours (e.g. 2.25).';
    else if (!positive(h)) errors.hours = 'Hours must be greater than 0.';
  }
  for (const l of form.lines) {
    if (l.removed || l.followsVolume) continue;
    const q = l.value.trim();
    if (!QUANTITY_PATTERN.test(q)) errors[`line:${l.eventId}`] = 'Up to 3 decimals.';
    else if (!positive(q)) errors[`line:${l.eventId}`] = 'Must be greater than 0. Use ✕ to remove.';
  }
  for (const a of form.adds) {
    const q = a.quantity.trim();
    if (!QUANTITY_PATTERN.test(q) || !positive(q)) {
      errors[`add:${a.clientId}`] = 'Enter a quantity greater than 0.';
    }
  }
  const reason = form.reason.trim();
  if (reason.length < REASON_MIN)
    errors.reason = `Give a reason (at least ${String(REASON_MIN)} characters).`;
  if (form.reason.length > REASON_MAX) errors.reason = `At most ${String(REASON_MAX)} characters.`;
  return errors;
}

const amount = (
  quantity: string,
  rateCents: number | null,
  ok: boolean,
  fallback: number,
): number => (ok ? lineAmountCents(quantity.trim(), rateCents ?? 0) : fallback);

/**
 * The ticket's unbilled lines as they would be after saving, priced with lineAmountCents,
 * and their total. A field that doesn't validate keeps its loaded amount.
 */
export function preview(
  page: TicketPageResponse,
  form: TicketEditForm,
): {
  readonly lines: ReadonlyMap<string, number>;
  readonly addCents: ReadonlyMap<string, number>;
  /** #52 — the disposal fee "Add disposal fee" would add; 0 when not adding. */
  readonly feeCents: number;
  readonly totalCents: number;
} {
  const lines = new Map<string, number>();
  for (const l of unbilled(page)) {
    let cents = l.amountCents;
    if (l.kind === 'VOLUME' && form.volume?.changesBill === true) {
      const v = form.volume.value;
      cents = amount(v, l.rateCents, BBL_PATTERN.test(v.trim()) && positive(v), cents);
    } else if (l.kind === 'HOURS' && form.hours !== null) {
      const h = form.hours.value;
      cents = amount(h, l.rateCents, HOURS_PATTERN.test(h.trim()) && positive(h), cents);
    } else {
      const edit = form.lines.find((e) => e.eventId === l.eventId);
      if (edit?.removed === true) cents = 0;
      else if (edit?.followsVolume === true) {
        cents = lineAmountCents(feeVolume(form, l.quantity), l.rateCents ?? 0);
      } else if (edit !== undefined) {
        cents = amount(
          edit.value,
          l.rateCents,
          QUANTITY_PATTERN.test(edit.value.trim()) && positive(edit.value),
          cents,
        );
      }
    }
    lines.set(l.eventId, cents);
  }
  const addCents = new Map<string, number>();
  for (const a of form.adds) {
    const ok = QUANTITY_PATTERN.test(a.quantity.trim()) && positive(a.quantity);
    addCents.set(a.clientId, amount(a.quantity, a.rateCents, ok, 0));
  }
  const feeCents =
    form.addFee && feeAddable(form.feeOffer)
      ? lineAmountCents(feeVolume(form, form.feeOffer.quantity), form.feeOffer.feeCentsPerBbl)
      : 0;
  let totalCents = feeCents;
  for (const c of lines.values()) totalCents += c;
  for (const c of addCents.values()) totalCents += c;
  return { lines, addCents, feeCents, totalCents };
}

export interface TicketEditBody {
  reason: string;
  netBbl?: { before: string; after: string };
  hours?: { before: string; after: string };
  lines?: { billableEventId: string; before: string; after: string }[];
  remove?: { billableEventId: string; before: string }[];
  add?: { clientId: string; serviceChargeId: string; quantity: string }[];
  /** #52 — the fee the form showed (the server refuses a different one as stale). */
  addDisposalFee?: { rateCents: number };
}

/** The PATCH body: only what changed. Null when nothing did. */
export function buildBody(form: TicketEditForm): TicketEditBody | null {
  const body: TicketEditBody = { reason: form.reason };
  let changed = false;
  if (form.volume !== null && !sameDecimal(form.volume.value, form.volume.loaded)) {
    body.netBbl = { before: form.volume.loaded, after: form.volume.value.trim() };
    changed = true;
  }
  if (form.hours !== null && !sameDecimal(form.hours.value, form.hours.loaded)) {
    body.hours = { before: form.hours.loaded, after: form.hours.value.trim() };
    changed = true;
  }
  const updates = form.lines
    .filter((l) => !l.removed && !sameDecimal(l.value, l.loaded))
    .map((l) => ({ billableEventId: l.eventId, before: l.loaded, after: l.value.trim() }));
  if (updates.length > 0) {
    body.lines = updates;
    changed = true;
  }
  const removals = form.lines
    .filter((l) => l.removed)
    .map((l) => ({ billableEventId: l.eventId, before: l.loaded }));
  if (removals.length > 0) {
    body.remove = removals;
    changed = true;
  }
  if (form.adds.length > 0) {
    body.add = form.adds.map((a) => ({
      clientId: a.clientId,
      serviceChargeId: a.serviceChargeId,
      quantity: a.quantity.trim(),
    }));
    changed = true;
  }
  if (form.addFee && feeAddable(form.feeOffer)) {
    body.addDisposalFee = { rateCents: form.feeOffer.feeCentsPerBbl };
    changed = true;
  }
  return changed ? body : null;
}

/** What a refused save means, in words (the API's 409 codes). */
export function saveErrorMessage(code: string | null): string {
  switch (code) {
    case 'TICKET_ON_INVOICE':
      return 'This ticket is now on an invoice. Void the invoice to edit it.';
    case 'TICKET_CHANGED':
      return 'Someone changed this ticket since you opened it. It has been reloaded; make your change again.';
    case 'TICKET_NOT_EDITABLE':
      return 'That change can’t be made to this ticket.';
    case 'PERMISSION_DENIED':
      return 'You don’t have permission to edit billed tickets.';
    default:
      return 'Couldn’t save the change. Try again.';
  }
}
